import {VisualizationType} from './visualization';
import {Filter, SimpleUnit} from '@core/interfaces/system/system-common';
import {GeospatialColourType} from './spatial-colours';

/*
 * Request
 */
export interface GeospatialTooltipRequest {
    tooltipType: GeospatialTooltipType;
    entityId: string;
    entityType?: string; // NOT the same as VisualizationType
    visualizationType?: VisualizationType;
}
export interface GeospatialMetricTooltipRequest extends GeospatialTooltipRequest {
    metrics?: string[]; // not using the defined metric types, such that more can be added w/o code changes
}
export interface GeospatialOutageTooltipRequest extends GeospatialTooltipRequest {
    outageMetrics?: string[];
    filters?: Filter[];
}

/*
 * Response
 */
export interface GeospatialTooltipResponse {
    tooltipType: GeospatialTooltipType;
    entityDetails: GeospatialEntityDetails;
    title: GeospatialTooltipTitle;
    sections: GeospatialTooltipSection[];
    centerCoordinate: CoordinateLatLong;
}

export interface GeospatialEntityDetails {
    entityId: string;
}

export interface GeospatialTooltipTitle {
    label: string;
    iconType: GeospatialIconType;
}

export interface CoordinateLatLong {
    latitude: number;
    longitude: number;
}

export interface GeospatialTooltipSection {
    order: number;
    label: string;
    fields: GeospatialTooltipField[];
}

export interface GeospatialTooltipField {
    label: string;
    value: string;
    dataType: FieldDataType;
    unit?: SimpleUnit;
    colourType?: GeospatialColourType;
}

/*
 * Enums
 */
export enum GeospatialTooltipType {
    // Geospatial viewer: metric, project, system layers
    METRIC_ASSET = 'METRIC_ASSET',
    METRIC_GROUP = 'METRIC_GROUP',
    PROJECT = 'PROJECT',
    SYSTEM_ENTITY = 'SYSTEM_ENTITY',
    // Asset Registry
    REGISTRY_ASSET = 'REGISTRY_ASSET',
    REGISTRY_GROUP = 'REGISTRY_GROUP',
    // Outage
    OUTAGE = 'OUTAGE',
    NONE = 'NONE',
    // Substations
    METRIC_SUBSTATION = 'METRIC_SUBSTATION',
}
export namespace GeospatialTooltipType {
    export function requiresStudyId(tooltipType: GeospatialTooltipType): boolean {
        switch (tooltipType) {
            case GeospatialTooltipType.METRIC_ASSET:
            case GeospatialTooltipType.METRIC_GROUP:
            case GeospatialTooltipType.PROJECT:
                return true;
            default:
                return false;
        }
    }
    export function isTypeProject(tooltipType: GeospatialTooltipType): boolean {
        return tooltipType === GeospatialTooltipType.PROJECT;
    }
    export function isTypeMetric(tooltipType: GeospatialTooltipType): boolean {
        switch (tooltipType) {
            case GeospatialTooltipType.METRIC_ASSET:
            case GeospatialTooltipType.METRIC_GROUP:
                return true;
            default:
                return false;
        }
    }
    export function isTypeOutage(tooltipType: GeospatialTooltipType): boolean {
        switch (tooltipType) {
            case GeospatialTooltipType.OUTAGE:
                return true;
            default:
                return false;
        }
    }
    export function isTypeSubstation(tooltipType: GeospatialTooltipType): boolean {
        switch (tooltipType) {
            case GeospatialTooltipType.METRIC_SUBSTATION:
                return true;
            default:
                return false;
        }
    }
}

export enum GeospatialIconType {
    POINT = 'POINT',
    LINE = 'LINE',
    POLYGON = 'POLYGON',
    NONE = 'NONE',
}

export enum FieldDataType {
    LONG = 'LONG',
    DOUBLE = 'DOUBLE',
    STRING = 'STRING',
    DATE = 'DATE',
    OPTIONS = 'OPTIONS',
    BOOLEAN = 'BOOLEAN',
}
